<template>
  <!-- The verses behind a count, fifty at a time. Apparatus verses link to their lemma. -->
  <div class="ref-list">
    <p v-if="!rows.length && loading" class="muted">Loading…</p>
    <ol>
      <li v-for="(r, i) in rows" :key="i" :class="{ 'is-unconfirmed': !r.confident }">
        <router-link v-if="r.lemma_id" :to="`/lemma/${r.lemma_id}`">{{ r.book }} {{ r.chapter }}:{{ r.verse }}</router-link>
        <span v-else>{{ r.book }} {{ r.chapter }}:{{ r.verse }}</span>
        <span class="surface font-greek">{{ r.surface }}</span>
        <OriginBadge :origin="r.origin" :witnesses="r.witnesses" :agrees-with-mt="r.agrees_with_mt" />
      </li>
    </ol>
    <button v-if="rows.length < total" class="plain" :disabled="loading" @click="more">
      {{ loading ? 'Loading…' : `${total - rows.length} more` }}
    </button>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, ref } from 'vue'
import { Language } from '@/components/lemmas/renderer/wordLayer'
import { Origin, refs, RefRow } from '../api'
import OriginBadge from './OriginBadge.vue'

export default defineComponent({
  name: 'RefList',
  components: { OriginBadge },
  props: {
    language: { type: String as PropType<Language>, required: true },
    lexeme: { type: String, required: true },
    column: { type: String, required: true },
    counterpart: { type: String as PropType<string | null>, default: null },
    /** Only the printed readings' words (head), a Var reading's, or an Attr line's; null: all. */
    origin: { type: String as PropType<Origin | null>, default: null },
  },
  setup(props) {
    const rows = ref<RefRow[]>([])
    const total = ref(0)
    const loading = ref(false)
    let page = 0
    const more = async () => {
      loading.value = true
      try {
        const p = await refs(props.language, props.lexeme, props.column, props.counterpart, page, props.origin)
        rows.value.push(...p.refs)
        total.value = p.total
        page += 1
      } finally {
        loading.value = false
      }
    }
    more()
    return { rows, total, loading, more }
  },
})
</script>

<style lang="scss" scoped>
@import '@/_colors';

.ref-list {
  margin: 0.25rem 0 0.5rem 0.75rem;
  padding-left: 0.5rem;
  border-left: 2px solid $border;
  font-size: 0.85rem;

  ol {
    list-style: none;
    margin: 0;
    columns: 2;
  }

  li {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
    break-inside: avoid;

    &.is-unconfirmed {
      font-style: italic;
    }
  }

  .surface,
  .muted {
    color: $text-light;
  }
}

.plain {
  border: 0;
  background: none;
  cursor: pointer;
  color: $interactive;
  font-size: 0.8rem;
  padding: 0;
}
</style>
